import React from 'react';
import { useHistory } from 'react-router-dom';

import StatusTrendChart from './StatusTrendChart';
import StatusComparisonChart from './StatusComparisonChart';
import { routes } from '../../../constants';

const formatNumber = (value) => Number(value || 0).toLocaleString('en');

const formatCurrency = (value) => {
  const amount = Number(value || 0);
  if (amount >= 1000000) return `$${(amount / 1000000).toFixed(1)}m`;
  if (amount >= 1000) return `$${(amount / 1000).toFixed(1)}k`;
  return `$${formatNumber(amount)}`;
};

const Comparison = ({ comparison }) => {
  if (!comparison) return null;
  const delta = Number(comparison.delta || 0);
  const positive = delta > 0;
  const negative = delta < 0;
  const percent = comparison.percent === null || comparison.percent === undefined
    ? ''
    : ` (${positive ? '+' : ''}${comparison.percent}%)`;

  return (
    <span className={positive ? 'is-positive' : negative ? 'is-negative' : ''}>
      {positive ? '+' : ''}{delta}{percent} vs prior
    </span>
  );
};

const SectionTitle = ({ code, children }) => (
  <div className="status-report-section__title">
    <b>{code}</b>
    <span>{children}</span>
  </div>
);

const Sparkline = ({ values = [] }) => {
  const max = Math.max(1, ...values);
  return (
    <div className="status-report-sparkline" aria-label={`Recent volume: ${values.join(', ')}`}>
      {values.map((value, index) => (
        <i key={index} style={{ height: `${Math.max(2, value / max * 22)}px` }} />
      ))}
    </div>
  );
};

const StatusReportView = ({ report, tenantLogo, tenantName, palette }) => {
  const history = useHistory();
  const { meta, outstandingPO } = report;

  return (
    <div className="status-report-document">
      <header className="status-report-header">
        <div>
          <h1>Rep Status Report</h1>
          <div>{meta.periodLabel}</div>
          <div>{meta.repName} - {meta.scopeLabel}</div>
        </div>
        <div className="status-report-header__meta">
          {tenantLogo ? (
            <img
              className="status-report-header__logo"
              src={tenantLogo}
              alt={`${tenantName || 'Tenant'} logo`}
            />
          ) : (
            <strong>{tenantName || 'CASE.'}</strong>
          )}
          <span>Data as at {meta.generatedAtLabel}</span>
          <span>{meta.comparisonLabel}</span>
        </div>
      </header>

      <div className="status-report-document__body">
        <section className="status-report-section">
          <SectionTitle code="A">Performance snapshot</SectionTitle>
          <div className="status-report-kpis">
            {report.kpis.map((kpi) => (
              <div key={kpi.id} className={`status-report-kpi ${kpi.id === 'outstanding' && outstandingPO.alert ? 'is-alert' : ''}`}>
                <div className="status-report-kpi__label">{kpi.label}</div>
                <div className="status-report-kpi__value">
                  {kpi.format === 'currency' ? formatCurrency(kpi.value) : formatNumber(kpi.value)}
                </div>
                <div className="status-report-kpi__comparison">
                  {kpi.note || <Comparison comparison={kpi.comparison} />}
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="status-report-section">
          <SectionTitle code="B">Case volume - completed and upcoming vs prior period</SectionTitle>
          <StatusTrendChart data={report.trend} palette={palette} />
          <p className="status-report-note">
            The prior-period line shows the full previous period. {report.cancelledCount} cancelled case(s) this period are excluded.
          </p>
        </section>

        <section className="status-report-section">
          <SectionTitle code="C">Cases by surgeon</SectionTitle>
          <div className="status-report-table-wrap">
            <table className="status-report-table">
              <thead>
                <tr>
                  <th>Surgeon</th>
                  <th>Hospital(s)</th>
                  <th className="num">Current</th>
                  <th className="num">Prior</th>
                  <th className="num">Delta</th>
                  <th className="num">Upcoming</th>
                  <th>Trend</th>
                </tr>
              </thead>
              <tbody>
                {report.surgeons.items.map((item) => (
                  <tr key={item.surgeonId} className={item.flag === 'quiet' ? 'is-quiet' : ''}>
                    <td>
                      <strong>{item.surgeonName}</strong>
                      {!!item.flag && <span className={`status-report-flag is-${item.flag}`}>{item.flag}</span>}
                    </td>
                    <td>{item.hospitals.join(', ') || '-'}</td>
                    <td className="num"><strong>{item.current}</strong></td>
                    <td className="num">{item.prior}</td>
                    <td className={`num ${item.delta > 0 ? 'is-positive' : item.delta < 0 ? 'is-negative' : ''}`}>
                      {item.delta > 0 ? '+' : ''}{item.delta}
                    </td>
                    <td className="num">{item.upcoming}</td>
                    <td><Sparkline values={item.trend} /></td>
                  </tr>
                ))}
                {!report.surgeons.items.length && (
                  <tr><td colSpan="7" className="status-report-table__empty">No surgeon activity in this period.</td></tr>
                )}
              </tbody>
            </table>
          </div>
          <p className="status-report-note">
            Quiet flags require at least 2 cases in the prior period and a decline of 50% or more.
            {report.surgeons.additionalCount > 0 ? ` ${report.surgeons.additionalCount} additional surgeon(s) are included in the PDF appendix.` : ''}
          </p>
        </section>

        <section className="status-report-section">
          <SectionTitle code="D">Outstanding POs - action list</SectionTitle>
          <div className="status-report-po-summary">
            <div>
              <strong>{outstandingPO.count}</strong> of {outstandingPO.completedCount} completed cases awaiting PO
              <b className={outstandingPO.alert ? 'is-negative' : ''}> ({outstandingPO.percent}%)</b>
            </div>
            <div className="status-report-po-chips">
              {Object.entries(outstandingPO.aging).map(([label, count]) => (
                <span key={label} className={label === '30+' ? 'is-hot' : ''}>{label} days - {count}</span>
              ))}
            </div>
          </div>
          <div className="status-report-table-wrap">
            <table className="status-report-table is-clickable">
              <thead>
                <tr>
                  <th>Case ref</th>
                  <th>Case date</th>
                  <th className="num">Days out</th>
                  <th>Hospital</th>
                  <th>Surgeon</th>
                  <th>Procedure</th>
                </tr>
              </thead>
              <tbody>
                {outstandingPO.items.map((item) => (
                  <tr key={item.caseId} onClick={() => history.push(`${routes.CASES}/${item.caseId}`)}>
                    <td><strong>{item.caseId}</strong></td>
                    <td>{item.dateLabel}</td>
                    <td className={`num ${item.daysOutstanding > 30 ? 'is-negative' : ''}`}>{item.daysOutstanding}</td>
                    <td>{item.hospitalName}</td>
                    <td>{item.surgeonName}</td>
                    <td>{item.procedureName}</td>
                  </tr>
                ))}
                {!outstandingPO.items.length && (
                  <tr><td colSpan="6" className="status-report-table__empty">No outstanding POs.</td></tr>
                )}
              </tbody>
            </table>
          </div>
          <p className="status-report-note">All outstanding POs visible to you, regardless of report period. Oldest first.</p>
        </section>

        <section className="status-report-section status-report-comparison-grid">
          <div>
            <SectionTitle code="E">Top procedures (completed)</SectionTitle>
            <StatusComparisonChart items={report.topProcedures.items} palette={palette} />
            <p className="status-report-note">Primary shade = current elapsed period. Light shade = prior elapsed period.</p>
          </div>
          <div>
            <SectionTitle code="F">Top hospitals (completed)</SectionTitle>
            <StatusComparisonChart items={report.topHospitals.items} palette={palette} />
            <p className="status-report-note">Includes hospitals with activity in either period.</p>
          </div>
        </section>

        {!!report.yearReview && (
          <section className="status-report-section">
            <SectionTitle code="G">Year in review</SectionTitle>
            <div className="status-report-year-grid">
              <div>
                <span>Cases YTD vs last year</span>
                <strong>{report.yearReview.current} <small>vs {report.yearReview.priorSamePoint}</small></strong>
                <p>Run-rate: {report.yearReview.projected} (prior actual: {report.yearReview.priorActual})</p>
              </div>
              <div>
                <span>Monthly volume</span>
                <div className="status-report-heat-strip">
                  {report.yearReview.monthly.map((item) => {
                    const max = Math.max(1, ...report.yearReview.monthly.map((month) => month.value));
                    return <i key={item.label} style={{ opacity: 0.16 + item.value / max * 0.84 }} title={`${item.value} cases`}><b>{item.label}</b></i>;
                  })}
                </div>
              </div>
              <div>
                <span>Surgeon movement</span>
                <p><b className="is-positive">New:</b> {report.yearReview.newSurgeons.map((item) => item.name).join(', ') || 'None'}</p>
                <p><b className="is-negative">Lost:</b> {report.yearReview.lostSurgeons.map((item) => item.name).join(', ') || 'None'}</p>
              </div>
            </div>
          </section>
        )}

        <footer className="status-report-method">
          Estimated pipeline value uses the configured average procedure value for upcoming cases and is not revenue.
          {!!report.pipelineMissingValueCount && ` ${report.pipelineMissingValueCount} case(s) have no value configured and contribute $0.`}
          {' '}KPI comparisons use the same elapsed point in the prior period.
        </footer>
      </div>
    </div>
  );
};

export default StatusReportView;
